import { BACKLOG_STATUS, FILTER_KEYS, NONE, PLANNING_FIELDS, PRIORITY_RANK, TASK_STATUSES } from "./constants";

/**
 * Pure board state logic, shared by REST responses, optimistic updates and
 * realtime events so they all follow the same rules:
 *
 *  - tasks are keyed by id → a task can never be rendered twice;
 *  - an incoming copy only replaces the local one if its `version` is newer,
 *    so duplicate, echoed (own mutation) or out-of-order events are ignored;
 *  - a task that no longer matches the active filters is dropped, one that
 *    starts matching is added.
 */

const idOf = (v) => (v && typeof v === "object" ? v._id : v) || null;
const idsOf = (list) => (Array.isArray(list) ? list.map(idOf).filter(Boolean) : []);

// Filter key → task field. Projects and assignees are lists: a task matches
// when the chosen value is one of them ("none" = empty list).
const FILTER_FIELD = { project: "projects", squad: "squad", sprint: "sprint", assignee: "assignees" };

/** Does `task` match the active board filters? */
export const matchesFilters = (task, filters = {}) => {
  for (const [key, field] of Object.entries(FILTER_FIELD)) {
    const wanted = filters[key];
    if (!wanted) continue;
    // Admins see the Product Backlog under any sprint filter (mirrors the API).
    if (key === "sprint" && filters.backlogAllSprints && task.status === BACKLOG_STATUS) continue;
    if (Array.isArray(task[field]) || field === "projects" || field === "assignees") {
      const ids = idsOf(task[field]);
      if (wanted === NONE ? ids.length > 0 : !ids.includes(wanted)) return false;
    } else {
      const actual = idOf(task[field]);
      if (wanted === NONE ? actual !== null : actual !== wanted) return false;
    }
  }
  if (filters.status && task.status !== filters.status) return false;
  if (filters.priority && task.priority !== filters.priority) return false;
  if (filters.q && !task.title?.toLowerCase().includes(filters.q.trim().toLowerCase())) {
    return false;
  }
  return true;
};

/** Read filters from URLSearchParams (only known keys). */
export const filtersFromParams = (params) =>
  Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ""]).filter(([, v]) => v));

export const initialBoardState = { tasks: {}, loaded: false };

export const boardReducer = (state, action) => {
  switch (action.type) {
    // Full (re)fetch. The server list is authoritative for membership, but a
    // copy that arrived over the socket while the request was in flight may be
    // newer than the response: keep the higher version.
    case "loaded": {
      const tasks = {};
      action.tasks.forEach((t) => {
        const local = state.tasks[t._id];
        tasks[t._id] = local && local.version > t.version ? local : t;
      });
      return { tasks, loaded: true };
    }

    // Authoritative copy from the server (REST response or socket event).
    case "upsert": {
      const { task, filters } = action;
      const current = state.tasks[task._id];
      if (current && current.version >= task.version) return state; // stale or duplicate
      const tasks = { ...state.tasks };
      if (matchesFilters(task, filters)) tasks[task._id] = task;
      else delete tasks[task._id];
      return { ...state, tasks };
    }

    // Optimistic local change (drag & drop). Version is kept, so the server's
    // copy (version + 1) still wins when it arrives.
    case "patchLocal": {
      const current = state.tasks[action.taskId];
      if (!current) return state;
      return {
        ...state,
        tasks: { ...state.tasks, [action.taskId]: { ...current, ...action.changes } },
      };
    }

    // Undo an optimistic change, unless a newer server copy already landed.
    case "rollback": {
      const current = state.tasks[action.snapshot._id];
      if (current && current.version !== action.snapshot.version) return state;
      return { ...state, tasks: { ...state.tasks, [action.snapshot._id]: action.snapshot } };
    }

    case "remove": {
      const current = state.tasks[action.taskId];
      if (!current) return state;
      // A removal older than what we hold (e.g. revoked, then re-granted) is stale.
      if (action.version && current.version > action.version) return state;
      const tasks = { ...state.tasks };
      delete tasks[action.taskId];
      return { ...state, tasks };
    }

    default:
      return state;
  }
};

/** Manual order value (older tasks without one fall back to their number). */
export const orderOf = (t) => (typeof t.position === "number" ? t.position : (t.number || 0) * 1000);

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** Deadline filter: overdue | today | week (today + next 6 days) | none. */
export const matchesDeadline = (task, mode, now = new Date()) => {
  if (!mode) return true;
  if (mode === "none") return !task.dueDate;
  if (!task.dueDate) return false;
  const day = startOfDay(new Date(task.dueDate));
  const today = startOfDay(now);
  if (mode === "overdue") return day < today && task.status !== "done";
  if (mode === "today") return day === today;
  if (mode === "week") return day >= today && day <= today + 6 * 86400000;
  return true;
};

/** Columns in workflow order, each in its manual (Trello-style) order, or by deadline (tasks without one last). */
export const groupByStatus = (tasksById, { deadline = "", sort = "", due = "" } = {}) => {
  const columns = Object.fromEntries(TASK_STATUSES.map((s) => [s.value, []]));
  const now = new Date();
  Object.values(tasksById).forEach((t) => {
    if (!matchesDeadline(t, deadline, now)) return;
    if (due && (!t.dueDate || localDay(new Date(t.dueDate)) !== due)) return;
    columns[t.status]?.push(t);
  });
  const [sortKey, sortDir] = sort.split("-");
  const dir = sortDir === "desc" ? -1 : 1;
  const byDeadline = (a, b) => {
    if (!a.dueDate || !b.dueDate) return (a.dueDate ? 0 : 1) - (b.dueDate ? 0 : 1);
    return dir * (new Date(a.dueDate) - new Date(b.dueDate));
  };
  // Priority: descending = highest first. Created: ascending = oldest first.
  const rank = (t) => PRIORITY_RANK[t.priority] ?? 99;
  const byPriority = (a, b) => -dir * (rank(a) - rank(b));
  const byCreated = (a, b) => dir * (new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
  const bySort = { deadline: byDeadline, priority: byPriority, created: byCreated }[sortKey];
  Object.values(columns).forEach((list) =>
    list.sort(
      (a, b) =>
        (bySort ? bySort(a, b) : 0) || orderOf(a) - orderOf(b) || (a.number || 0) - (b.number || 0),
    ),
  );
  return columns;
};

/**
 * Order value for dropping `draggedId` at `index` of a column's (sorted)
 * task list: between its new neighbours, so only the moved card changes.
 */
export const positionAt = (tasks, index, draggedId) => {
  const list = tasks.filter((t) => t._id !== draggedId);
  const prev = list[index - 1];
  const next = list[index];
  if (!prev && !next) return 1000;
  if (!prev) return orderOf(next) - 1000;
  if (!next) return orderOf(prev) + 1000;
  return (orderOf(prev) + orderOf(next)) / 2;
};

/** Bounded set of processed realtime event ids (protects against re-delivery). */
export const createEventDeduper = (max = 500) => {
  const seen = new Set();
  return (eventId) => {
    if (!eventId) return false;
    if (seen.has(eventId)) return true;
    seen.add(eventId);
    if (seen.size > max) seen.delete(seen.values().next().value);
    return false;
  };
};

/** Access helpers mirroring the backend rules (UI affordances only; the server enforces). */
export const roleIn = (access, squadId) =>
  access?.memberships?.find((m) => m.squadId === idOf(squadId))?.role || null;

export const canPlanTask = (access, task) =>
  Boolean(access?.isAdmin || roleIn(access, task?.squad) === "SQUAD_LEAD");

export const TASK_FIELDS = [
  "title",
  "description",
  "taskType",
  "squad",
  "assignees",
  "projects",
  "sprint",
  "status",
  "priority",
  "dueDate",
];

/**
 * Fields `access` may edit on an existing task (mirrors the backend):
 * admin → all; squad lead → all but type/squad/sprint/due date; member (own task) → status.
 */
export const editableTaskFields = (access, task) => {
  if (access?.isAdmin) return new Set(TASK_FIELDS);
  const role = roleIn(access, task?.squad);
  if (role === "SQUAD_LEAD") {
    return new Set(TASK_FIELDS.filter((f) => !["taskType", "squad", "sprint", "dueDate"].includes(f)));
  }
  if (role === "MEMBER" && idsOf(task?.assignees).includes(access?.userId)) return new Set(["status"]);
  return new Set();
};

const localDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
// Sprint dates are picked as calendar days (date inputs → UTC midnight).
const sprintDay = (value) => new Date(value).toISOString().slice(0, 10);

/**
 * The sprint running today by its dates (start and end days inclusive),
 * regardless of its status. If several overlap, the most recently started wins.
 */
export const currentSprint = (sprints = [], now = new Date()) => {
  const today = localDay(now);
  return (
    sprints
      .filter((s) => s.startDate && s.endDate && sprintDay(s.startDate) <= today && today <= sprintDay(s.endDate))
      .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))[0] || null
  );
};

/**
 * Which optional board filters make sense for this user:
 *  - squad: admins, or anyone in more than one squad (one squad = already scoped);
 *  - assignee: admins and squad leads (members only ever see their own tasks).
 */
// Everyone sees every squad's tasks, so everyone can filter by squad / assignee.
// eslint-disable-next-line no-unused-vars
export const filterVisibility = (_access) => ({ squad: true, assignee: true });

/**
 * Statuses a user works with. Product Backlog is admin-only, so squad leads
 * and members never see it (columns, filters, status pickers, counts).
 */
export const statusesFor = (access) =>
  access?.isAdmin ? TASK_STATUSES : TASK_STATUSES.filter((s) => s.value !== BACKLOG_STATUS);

/** Planning fields still empty on `task`. The squad is optional, so it is never "missing". */
export const missingPlanningFields = (task) =>
  PLANNING_FIELDS.filter((f) => (Array.isArray(task?.[f]) ? task[f].length === 0 : !idOf(task?.[f])));

/**
 * Does moving `task` to `toStatus` need the planning modal? Leaving Product
 * Backlog always does; other moves only when older data is incomplete.
 */
export const needsPlanning = (task, toStatus) =>
  toStatus !== BACKLOG_STATUS &&
  (task.status === BACKLOG_STATUS || missingPlanningFields(task).length > 0);

export const leadSquadIds = (access) =>
  (access?.memberships || []).filter((m) => m.role === "SQUAD_LEAD").map((m) => m.squadId);
